import { useState } from "react";

import { useNavigation } from "@refinedev/core";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Copy,
  Globe,
  PlayCircle,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import { scheduledCitiesText, scheduledSitesText } from "./lib";
import type { CountryCoverage, CuratorRef } from "./lib";
import { useScheduledCoverage, useTodayCoverage } from "./use-today-coverage";

function Avatars({ curators }: { curators: CuratorRef[] }) {
  return (
    <span className="flex items-center">
      {curators.map((c, i) => (
        <Avatar
          key={c.uid}
          className={cn("h-6 w-6", i > 0 && "-ml-1.5")}
          style={{ boxShadow: `0 0 0 2px var(--card), 0 0 0 3px ${c.color}` }}
          title={c.displayName}
        >
          <AvatarImage src={c.photoUrl ?? undefined} />
          <AvatarFallback className="text-[10px]">
            {c.displayName.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      ))}
    </span>
  );
}

function CountryRow({ country }: { country: CountryCoverage }) {
  const [open, setOpen] = useState(false);
  return (
    // `border-t` on EVERY cell, not `first:border-t-0`: in a grid the first child is
    // only the first column, which would leave the other columns' opening rows
    // unruled and the row of lines ragged.
    <div className="border-t">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left hover:bg-muted/50"
      >
        <ChevronRight
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-90",
          )}
        />
        <span className="text-base leading-none">{country.flag}</span>
        <span className="truncate font-medium">{country.countryName}</span>
        <span className="truncate text-[11px] text-muted-foreground tabular-nums">
          · {country.siteCount} site{country.siteCount === 1 ? "" : "s"} ·{" "}
          {country.cityCount} cit{country.cityCount === 1 ? "y" : "ies"}
        </span>
        <span className="flex-1" />
        <Avatars curators={country.curators} />
      </button>
      {open && (
        <div className="flex flex-col gap-2 pb-2 pl-7 pr-1">
          {country.cities.map((city) => (
            <div key={city.city} className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{city.city}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {city.siteCount} site{city.siteCount === 1 ? "" : "s"}
                </span>
                <span className="flex-1" />
                <Avatars curators={city.curators} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {city.sites.map((s) => (
                  <span
                    key={s.name}
                    className="rounded-md border bg-muted/40 px-1.5 py-0.5 font-mono text-[10.5px] text-muted-foreground"
                  >
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const TodayCoverageCard = ({ className }: { className?: string }) => {
  const { list, create } = useNavigation();
  const { data, isLoading, isError } = useTodayCoverage();
  const empty = !isLoading && (!data || data.totalSites === 0);
  const scheduled = useScheduledCoverage();
  const [copied, setCopied] = useState<"cities" | "sites" | null>(null);

  const copy = async (what: "cities" | "sites", text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error(`Failed to copy ${what}:`, err);
    }
  };

  const copyCities = () => {
    if (scheduled) copy("cities", scheduledCitiesText(scheduled));
  };

  const copySites = () => {
    if (scheduled) copy("sites", scheduledSitesText(scheduled));
  };

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4 text-muted-foreground" />
            Today's coverage
          </CardTitle>
          <CardDescription>
            {data && data.totalSites > 0
              ? `${data.totalCountries} ${data.totalCountries === 1 ? "country" : "countries"} · ${data.totalSites} sites · ${data.totalCurators} ${data.totalCurators === 1 ? "runner" : "runners"}`
              : "What the team has run so far today"}
          </CardDescription>
        </div>
        {/* No "Today" badge: the card title already says it, and the header
            carries the counts the badge would have restated. */}
        <div className="flex flex-wrap items-center justify-end gap-1">
          {/* Copies what the site groups schedule, not what this card shows:
              it is there because the card is where the team looks at coverage. */}
          {scheduled && scheduled.length > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-xs"
                onClick={copyCities}
                title="Copy every country - city the site groups run on a schedule, split by vertical"
              >
                {copied === "cities" ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                {copied === "cities" ? "Copied" : "Copy scheduled cities"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-xs"
                onClick={copySites}
                title="Copy every scheduled site and the Instagram handle it runs under, grouped by vertical, country and city"
              >
                {copied === "sites" ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
                {copied === "sites" ? "Copied" : "Copy scheduled sites"}
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            onClick={() => list("tina-runs")}
          >
            All runs today
            <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3 py-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            Couldn't load today's coverage.
          </div>
        ) : empty ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
            <div className="text-sm font-medium">No runs yet today</div>
            <Button
              size="sm"
              className="gap-1.5"
              onClick={() => create("tina-runs")}
            >
              <PlayCircle className="h-4 w-4" />
              Trigger a run
            </Button>
          </div>
        ) : (
          // The rollout keeps adding countries, and one per line made this card the
          // tallest thing on the page. Columns trade width for height; the card is
          // full-width, so there is width to spend.
          <div className="grid grid-cols-1 gap-x-6 md:grid-cols-2 xl:grid-cols-3">
            {data!.countries.map((c) => (
              <CountryRow key={c.countryCode} country={c} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
